import React, { useState } from 'react';
import { X, Copy, Check, Send, Mail } from 'lucide-react';
import { restFetch } from './restui';
import { getRef } from '../../lib/referral';

/* Two distinct doors, one modal:
   mode="self"  → "Email me the link": YOUR permanent personal link (UTM-tagged), Brevo + mailto fallback.
   mode="share" → "Share demo": WhatsApp / X / copy / "Rachel emails your friend". */

const WA_ICON = (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="#25D366" aria-hidden="true">
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.2 14.2c-.2.6-1.2 1.2-1.7 1.2-.4.1-1 .1-1.6-.1a13 13 0 0 1-1.5-.5c-2.6-1.1-4.3-3.7-4.4-3.9-.1-.2-1-1.4-1-2.6 0-1.2.6-1.8.9-2 .2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2c.1.2.1.4 0 .6l-.4.6-.4.4c-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.4 2.4 1.5.3.1.5.1.6-.1l.8-1c.2-.3.4-.2.6-.1l1.9.9c.3.1.4.2.5.3 0 .2 0 .7-.2 1Z" />
  </svg>
);
const X_ICON = (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
    <path d="M18.9 2H22l-6.8 7.8L23.3 22h-6.3l-4.9-6.4L6.4 22H3.3l7.3-8.3L1 2h6.4l4.4 5.9L18.9 2Zm-1.1 18h1.7L7.3 3.8H5.5L17.8 20Z" />
  </svg>
);

export function ShareDemoModal({ isEs, onClose, profileKey = '', mode: initialMode = 'share' }) {
  const [mode, setMode] = useState(initialMode === 'self' ? 'self' : 'share');
  const [done, setDone] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [myEmail, setMyEmail] = useState('');
  const [selfBusy, setSelfBusy] = useState(false);
  const [selfErr, setSelfErr] = useState('');
  const [selfLink, setSelfLink] = useState('');
  const [selfSent, setSelfSent] = useState(false);
  const [vStatus, setVStatus] = useState('idle'); // idle | building | ready | error
  const [vUrl, setVUrl] = useState('');
  const API = process.env.REACT_APP_BACKEND_URL;
  const lang = isEs ? 'es' : 'en';
  React.useEffect(() => {
    if (mode !== 'share') return;
    fetch(`${API}/api/rest/demo/teaser/status?lang=${lang}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && j.status === 'ready') { setVStatus('ready'); setVUrl(`${API}${j.url}`); } })
      .catch(() => {});
  }, [API, lang, mode]);
  const buildVideo = async () => {
    setVStatus('building');
    try {
      const r = await fetch(`${API}/api/rest/demo/teaser/build`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang }) });
      if (!r.ok) throw new Error();
      const j = await r.json();
      if (j.status === 'ready') { setVStatus('ready'); setVUrl(`${API}${j.url}`); return; }
      const poll = setInterval(async () => {
        try {
          const s = await (await fetch(`${API}/api/rest/demo/teaser/status?lang=${lang}`, { credentials: 'include' })).json();
          if (s.status === 'ready') { clearInterval(poll); setVStatus('ready'); setVUrl(`${API}${s.url}`); }
          else if (s.status === 'error') { clearInterval(poll); setVStatus('error'); }
        } catch { /* keep polling */ }
      }, 3500);
      setTimeout(() => clearInterval(poll), 240000);
    } catch { setVStatus('error'); }
  };
  // The partner behind this visit keeps the credit on every forward — real code, never a placeholder.
  // The link also carries the chosen cuisine, so the friend walks straight into THEIR kind of restaurant.
  const shareQ = [getRef() ? `ref=${getRef()}` : '', profileKey ? `profile=${profileKey}` : ''].filter(Boolean).join('&');
  const shareUrl = `${window.location.origin}/demo${shareQ ? `?${shareQ}` : ''}`;

  const sendSelf = async () => {
    const em = myEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setSelfErr(isEs ? 'Revisa tu email' : 'Check your email'); return; }
    setSelfBusy(true); setSelfErr('');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-access/send-link`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: em, locale: isEs ? 'es' : 'en' }) });
      const j = await r.json();
      if (!r.ok) throw new Error();
      setSelfLink(j.link); setSelfSent(!!j.sent);
    } catch {
      setSelfErr(isEs ? 'No se pudo enviar — inténtalo de nuevo' : "Couldn't send — try again");
    } finally { setSelfBusy(false); }
  };
  const mailtoSelf = selfLink
    ? `mailto:${encodeURIComponent(myEmail.trim())}?subject=${encodeURIComponent(isEs ? 'Mi enlace personal a la demo de Kitxens' : 'My personal Kitxens demo link')}&body=${encodeURIComponent((isEs ? 'Tu enlace personal permanente — vuelve cuando quieras:\n\n' : 'Your permanent personal link — come back anytime:\n\n') + selfLink)}`
    : '';
  const msg = isEs
    ? `Acabo de ver esto y me acordé de ti: un sistema con IA para restaurantes — te dice qué plato empuja tu revenue, responde tus reseñas solo y de dónde viene cada cliente. La demo es real y sin registro: ${shareUrl}`
    : `Just saw this and thought of you: an AI system for restaurants — it tells you which dish drives your revenue, answers your reviews on its own, and where every guest comes from. The demo is real, no signup: ${shareUrl}`;

  const track = (channel, friendEmail) => restFetch('/demo/share', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, friend_email: friendEmail || undefined, locale: isEs ? 'es' : 'en', profile: profileKey || undefined }),
  });

  const share = (channel) => {
    track(channel).catch(() => {});
    if (channel === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    if (channel === 'x') window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`, '_blank');
    if (channel === 'copy') navigator.clipboard.writeText(msg).catch(() => {});
    setDone(channel);
  };

  const sendEmail = async () => {
    const em = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setErr(isEs ? 'Revisa el email' : 'Check the email'); return; }
    setBusy(true); setErr('');
    try {
      const r = await track('email', em);
      const j = await r.json();
      if (!r.ok) throw new Error();
      setDone(j.sent ? 'email' : 'email_soft');
      setEmail('');
    } catch {
      setErr(isEs ? 'No se pudo enviar — copia el link y mándaselo tú' : "Couldn't send — copy the link and send it yourself");
    } finally { setBusy(false); }
  };

  const DONE_MSG = {
    whatsapp: isEs ? 'Abierto en WhatsApp — le acabas de hacer un favor.' : 'Opened in WhatsApp — you just did them a favor.',
    x: isEs ? 'Listo para publicar en X.' : 'Ready to post on X.',
    copy: isEs ? 'Mensaje copiado — pégalo donde quieras.' : 'Message copied — paste it anywhere.',
    email: isEs ? 'Enviado — Rachel le acaba de escribir de tu parte.' : 'Sent — Rachel just wrote to them on your behalf.',
    email_soft: isEs ? 'Registrado — le escribiremos enseguida.' : 'Registered — we will write to them shortly.',
  };
  const isSelf = mode === 'self';

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-demo-modal">
      <div className="bg-white rounded-3xl p-7 w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--rest-accent)] font-bold">
              {isSelf ? (isEs ? 'Tu enlace personal' : 'Your personal link') : (isEs ? 'Compartir demo' : 'Share demo')}
            </p>
            <h2 className="mt-2 text-[21px] text-gray-900 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
              {isSelf
                ? (isEs ? 'Llévate la demo contigo.' : 'Take the demo with you.')
                : (isEs ? '¿A quién le puede ayudar esto?' : 'Who could this help?')}
            </h2>
            <p className="mt-1.5 text-[13px] text-gray-500">
              {isSelf
                ? (isEs ? 'Te enviamos TU enlace permanente por email — vuelve cuando quieras, sin registro.' : 'We email YOU your permanent link — come back anytime, no sign-up.')
                : (isEs ? 'Piensa en ese amigo con restaurante que siempre anda apagando fuegos.' : 'Think of that friend with a restaurant who is always putting out fires.')}
            </p>
          </div>
          <button onClick={onClose} data-testid="share-demo-close" className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {isSelf && (
          <>
            <div className="mt-5 rounded-2xl border border-[var(--rest-accent)]/30 bg-[var(--rest-accent-soft)] p-4" data-testid="share-demo-selflink">
              <p className="text-[12px] font-semibold text-gray-800">
                {isEs ? 'Tu enlace lleva tu nombre: cada visita queda atribuida a ti.' : 'Your link carries your name: every visit is attributed to you.'}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {isEs ? 'Y si lo reenvías, tu contacto entra invitado de tu parte.' : 'Forward it and your contact walks in as your guest.'}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <input type="email" value={myEmail} onChange={(e) => setMyEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendSelf()} autoFocus
                  placeholder={isEs ? 'tu@email.com' : 'you@email.com'} data-testid="share-demo-self-input"
                  className="flex-1 h-10 rounded-full border border-gray-200 bg-white px-4 text-[13px] outline-none focus:border-[var(--rest-accent)] transition-colors" />
                <button onClick={sendSelf} disabled={selfBusy} data-testid="share-demo-self-send"
                  className="h-10 shrink-0 rounded-full bg-[var(--rest-accent)] text-white px-4 text-[12px] font-bold flex items-center gap-1.5 hover:brightness-110 transition-all disabled:opacity-50">
                  <Send className="w-3.5 h-3.5" /> {selfBusy ? '…' : (isEs ? 'Envíamelo' : 'Send it to me')}
                </button>
              </div>
              {selfErr && <p className="mt-2 text-[11.5px] text-red-500" data-testid="share-demo-self-error">{selfErr}</p>}
              {selfLink && (
                <div className="mt-2.5" data-testid="share-demo-self-done">
                  <p className="text-[12px] font-semibold text-emerald-600">
                    ✓ {selfSent
                      ? (isEs ? 'Enviado — revisa tu bandeja. Este es tu enlace:' : 'Sent — check your inbox. This is your link:')
                      : (isEs ? 'Este es tu enlace personal (guárdalo o cópialo):' : 'This is your personal link (save or copy it):')}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-white border border-gray-200 px-3 py-2">
                    <a href={selfLink} target="_blank" rel="noreferrer" data-testid="share-demo-self-open"
                      className="flex-1 text-[11.5px] font-mono text-gray-700 truncate underline decoration-dotted underline-offset-2 hover:text-[var(--rest-accent)] transition-colors">{selfLink}</a>
                    <button onClick={() => { navigator.clipboard.writeText(selfLink).catch(() => {}); setDone('selfcopy'); }}
                      data-testid="share-demo-self-copy"
                      className="text-[10.5px] font-bold uppercase tracking-wide text-[var(--rest-accent)] shrink-0">
                      {done === 'selfcopy' ? (isEs ? '¡Copiado!' : 'Copied!') : (isEs ? 'Copiar' : 'Copy')}
                    </button>
                  </div>
                  {!selfSent && (
                    <a href={mailtoSelf} data-testid="share-demo-self-mailto"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--rest-accent)]/50 text-[var(--rest-accent)] text-[11.5px] font-bold px-3.5 py-1.5 hover:bg-white transition-colors">
                      <Mail className="w-3.5 h-3.5" /> {isEs ? 'Abrir mi correo y guardarlo' : 'Open my mail app and save it'}
                    </a>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setMode('share')} data-testid="share-demo-switch-share"
              className="mt-4 w-full text-center text-[12px] font-bold text-gray-400 hover:text-[var(--rest-accent)] transition-colors">
              {isEs ? '¿Quieres compartir la demo con alguien? →' : 'Want to share the demo with someone? →'}
            </button>
          </>
        )}

        {!isSelf && (
          <>
            <div className="mt-5 grid grid-cols-3 gap-2">
              <button onClick={() => share('whatsapp')} data-testid="share-demo-whatsapp"
                className="rounded-2xl border border-gray-200 py-3 flex flex-col items-center gap-1.5 text-[11.5px] font-bold text-gray-700 hover:border-[#25D366] hover:text-[#1DA851] transition-colors">
                {WA_ICON} WhatsApp
              </button>
              <button onClick={() => share('x')} data-testid="share-demo-x" aria-label={isEs ? 'Compartir en X' : 'Share on X'}
                className="rounded-2xl border border-gray-200 py-3 flex flex-col items-center justify-center gap-1.5 text-gray-900 hover:border-gray-900 transition-colors">
                {X_ICON}
                <span className="text-[11.5px] font-bold text-gray-700">{isEs ? 'Publicar' : 'Post'}</span>
              </button>
              <button onClick={() => share('copy')} data-testid="share-demo-copy"
                className="rounded-2xl border border-gray-200 py-3 flex flex-col items-center gap-1.5 text-[11.5px] font-bold text-gray-700 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors">
                {done === 'copy' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {isEs ? 'Copiar' : 'Copy'}
              </button>
            </div>

            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4" data-testid="share-demo-video">
              <p className="text-[12px] font-semibold text-gray-800">
                🎬 {isEs ? 'Vídeo de la demo (35s) — perfecto para WhatsApp' : 'Demo teaser video (35s) — perfect for WhatsApp'}
              </p>
              {vStatus === 'ready' && vUrl ? (
                <div className="mt-2">
                  <video src={vUrl} controls preload="metadata" className="w-full rounded-xl border border-gray-200" data-testid="share-demo-video-player" />
                  <div className="mt-2 flex items-center gap-3">
                    <a href={vUrl} download="kitxens-demo.mp4" data-testid="share-demo-video-download"
                      className="rounded-full bg-[var(--rest-accent)] text-white text-[12px] font-bold px-4 py-1.5 hover:brightness-110 transition-all">
                      {isEs ? 'Descargar' : 'Download'}
                    </a>
                    <p className="text-[11px] text-gray-500">{isEs ? 'Descárgalo y compártelo junto a tu enlace.' : 'Download it and share it along with your link.'}</p>
                  </div>
                </div>
              ) : vStatus === 'building' ? (
                <p className="mt-2 text-[12px] text-gray-500 animate-pulse" data-testid="share-demo-video-status">
                  {isEs ? 'Rachel está montando tu vídeo… (~1 min, puedes cerrar y volver)' : 'Rachel is cutting your video… (~1 min, you can close and come back)'}
                </p>
              ) : vStatus === 'error' ? (
                <p className="mt-2 text-[12px] text-red-500" data-testid="share-demo-video-status">
                  {isEs ? 'No se pudo generar — inténtalo de nuevo en un rato.' : "Couldn't generate — try again in a bit."}
                </p>
              ) : (
                <button onClick={buildVideo} data-testid="share-demo-video-build"
                  className="mt-2 rounded-full border border-[var(--rest-accent)]/60 text-[var(--rest-accent)] text-[12px] font-bold px-4 py-1.5 hover:bg-[var(--rest-accent-soft)] transition-colors">
                  {isEs ? 'Generar mi vídeo' : 'Generate my video'}
                </button>
              )}
            </div>

            <div className="mt-4 rounded-2xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-[12px] font-semibold text-gray-700">
                {isEs ? 'O dime su email y le escribo yo — Rachel' : "Or give me their email and I'll write to them — Rachel"}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendEmail()}
                  placeholder={isEs ? 'email de tu amigo' : "your friend's email"} data-testid="share-demo-email-input"
                  className="flex-1 h-10 rounded-full border border-gray-200 bg-white px-4 text-[13px] outline-none focus:border-[var(--rest-accent)] transition-colors" />
                <button onClick={sendEmail} disabled={busy} data-testid="share-demo-email-send"
                  className="h-10 w-10 shrink-0 rounded-full bg-[var(--rest-accent)] text-white flex items-center justify-center hover:brightness-110 transition-all disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </div>
              {err && <p className="mt-2 text-[11.5px] text-red-500" data-testid="share-demo-error">{err}</p>}
            </div>
            <button onClick={() => setMode('self')} data-testid="share-demo-switch-self"
              className="mt-4 w-full text-center text-[12px] font-bold text-gray-400 hover:text-[var(--rest-accent)] transition-colors">
              {isEs ? '¿Lo quieres para TI? Envíatelo a tu email →' : 'Want it for YOURSELF? Email me the link →'}
            </button>
          </>
        )}

        {done && DONE_MSG[done] && (
          <p className="mt-4 text-[12.5px] font-semibold text-emerald-600 text-center" data-testid="share-demo-done">
            ✓ {DONE_MSG[done]}
          </p>
        )}
      </div>
    </div>
  );
}
